import React, { useMemo, useState } from "react";
import { FiChevronRight } from "react-icons/fi";
import { ModalCompAdvance } from "../../../../components";
import { formatNaira } from "../../../../utils/dashboardFormat";
import LoanDetailsModal from "../../Home/components/LoanDetailsModal";
import { Notice, PanelHead, StatusPill, formatDay, titleCase } from "../../../../components/MemberProfile/parts";
import { flattenLoans, sectionState } from "../../../../components/MemberProfile/profileModel";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "active", label: "Active", match: (s) => s === "disbursed" || s === "approved" },
  { key: "pending", label: "Pending", match: (s) => s === "pending" },
  { key: "paid", label: "Paid", match: (s) => s === "paid" },
  { key: "declined", label: "Declined", match: (s) => s === "declined" },
];

/**
 * `trustTotal`: the staff page gets the full list and the total from one response, so a total
 * with no loans behind it means the list failed to load. The member page only fetches some
 * statuses (no paid loans), so there a missing list is just an empty one.
 */
const LoansTab = ({ loans = {}, totalsByStatus = {}, total = 0, trustTotal = true }) => {
  const [selectedLoanId, setSelectedLoanId] = useState(null);
  const [filter, setFilter] = useState("all");

  const rows = useMemo(() => flattenLoans(loans), [loans]);
  const knownTotal = Number(total) || Object.values(totalsByStatus || {}).reduce((sum, v) => sum + (Number(v) || 0), 0);
  const state = sectionState({ rows: rows.length, total: trustTotal ? knownTotal : 0 });

  if (state === "empty") {
    return (
      <Notice title="No loans yet">Loan applications from this member will show up here.</Notice>
    );
  }
  if (state === "missing") {
    return (
      <Notice
        tone="warn"
        title="Couldn't load the loan list"
        actionLabel="Reload"
        onAction={() => window.location.reload()}
      >
        This member has {formatNaira(knownTotal)} in loans on record, but the individual loans didn't come through.
      </Notice>
    );
  }

  const active = FILTERS.find((f) => f.key === filter);
  const shown = active?.match ? rows.filter((r) => active.match(r.status)) : rows;
  const countFor = (f) => (f.match ? rows.filter((r) => f.match(r.status)).length : rows.length);

  return (
    <div>
      <PanelHead
        title="Loans"
        stats={[
          { label: "Taken in total", value: formatNaira(knownTotal), tone: "amber" },
          { label: "Active balance", value: formatNaira(Number(totalsByStatus?.disbursed) || 0), quiet: !Number(totalsByStatus?.disbursed) },
          { label: "Awaiting decision", value: formatNaira(Number(totalsByStatus?.pending) || 0), quiet: !Number(totalsByStatus?.pending) },
        ]}
      />
      <div className="mp-filters" role="group" aria-label="Filter loans by status">
        {FILTERS.filter((f) => f.key === "all" || countFor(f) > 0).map((f) => (
          <button
            key={f.key}
            type="button"
            className={`mp-chip ${filter === f.key ? "on" : ""}`}
            aria-pressed={filter === f.key}
            onClick={() => setFilter(f.key)}
          >
            {f.label} {countFor(f)}
          </button>
        ))}
      </div>
      <div className="mp-scroll">
        <table className="mp-table">
          <thead>
            <tr>
              <th>Loan</th>
              <th className="num">Amount</th>
              <th>Term</th>
              <th>Applied</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {shown.map((loan, i) => (
              <tr key={loan.id ?? i}>
                <td>
                  {titleCase(loan.type)}
                  {loan.reference ? <span className="sub">{loan.reference}</span> : null}
                </td>
                <td className="num">{formatNaira(loan.amount)}</td>
                <td>{loan.duration > 0 ? `${loan.duration} ${titleCase(loan.durationType)}` : "—"}</td>
                <td>{formatDay(loan.createdAt)}</td>
                <td>
                  <StatusPill status={loan.status} />
                </td>
                <td className="num">
                  {loan.id ? (
                    <button type="button" className="mp-link" onClick={() => setSelectedLoanId(loan.id)}>
                      Details <FiChevronRight size={14} aria-hidden="true" />
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ModalCompAdvance
        show={!!selectedLoanId}
        handleClose={() => setSelectedLoanId(null)}
        close
        size="xl"
        centered
        modalBody={0}
        arrayComp={[
          {
            component: selectedLoanId ? (
              <LoanDetailsModal loanId={selectedLoanId} handleClose={() => setSelectedLoanId(null)} />
            ) : null,
          },
        ]}
      />
    </div>
  );
};

export default LoansTab;
